import React, { FunctionComponent, useState } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import IconButton from '@material-ui/core/IconButton'
import IconLink from '@material-ui/icons/Link'
import IconLinkOff from '@material-ui/icons/LinkOff'
import MuiTextField from '@material-ui/core/TextField'
import { withStyles } from '@material-ui/core/styles'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { addDays, addMonths, format, subMinutes } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { useSubscriptions } from 'components/views/subscriptions/subscriptions/SubscriptionsProvider'
import { TextField } from 'components/elements/TextField'
import { DurationType } from 'models/ZoneOfferModel'
import { formatDateFieldToISO, formatToDateField, isDateFieldValid, parseDateField, yupDateField } from 'utils'
import { DateField } from 'Elements/DateField'

const EndTimeField = withStyles(theme => ({
  root: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: theme.palette.text.primary,
    },
  },
}))(MuiTextField)

export interface EditSubscriptionDialogData {
  id: number
  validFrom: string
  validTill: string
  durationType: DurationType
  durationLength: number
  count: number
  plateNumbers: string[]
  clients: string[]
  notes: string
  updatable: string[]
}

const linkableDurationTypes = [
  DurationType.daysAbsolute,
  DurationType.monthsAbsolute,
  DurationType.monthsRelative,
]

const deriveValidTill = (validFrom: string, durationType: DurationType, durationLength: number) => {
  if (!validFrom || !isDateFieldValid(validFrom)) {
    return null
  }

  switch (durationType) {
    case DurationType.daysAbsolute:
      return subMinutes(addDays(parseDateField(validFrom), durationLength), 1)

    case DurationType.monthsAbsolute:
    case DurationType.monthsRelative:
      return subMinutes(addMonths(parseDateField(validFrom), durationLength), 1)

    default:
      return null
  }
}

const deriveValidTillField = (validFrom: string, durationType: DurationType, durationLength: number) => {
  const validTill = deriveValidTill(validFrom, durationType, durationLength)

  return validTill ? format(validTill, 'dd.MM.yyyy') : ''
}

type Props = {
  open: boolean
  data: EditSubscriptionDialogData
}
type Functions = {
  setOpen(value: boolean): void
}
type AllProps = Props & Functions

const EditSubscriptionDialog: FunctionComponent<AllProps> = ({ open, setOpen, data }) => {
  if (!data) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { setLoadList } = useSubscriptions()
  const { showSuccess, showError } = useMessage()

  const [linked, setLinked] = useState(false)

  const updateValidPeriod = data.updatable.includes('valid_period')
  const updateCount = data.updatable.includes('count')
  const updateClients = data.updatable.includes('clients')

  const linkable = linkableDurationTypes.indexOf(data.durationType) >= 0

  const handleEnter = () => {
    if (updateValidPeriod) {
      const validFrom = formatToDateField(data.validFrom)
      const validTill = data.validTill ? formatToDateField(data.validTill) : ''

      formal.change('validFrom', validFrom)
      if (validTill) {
        formal.change('validTill', validTill)
      }

      setLinked(linkable && validTill == deriveValidTillField(validFrom, data.durationType, data.durationLength))
    }
    if (updateCount) {
      formal.change('count', data.count)
    }
    formal.change('plateNumbers', Array.from(data.plateNumbers).sort().join(', '))
    if (updateClients) {
      formal.change('clients', Array.from(data.clients).join(', '))
    }
    formal.change('notes', data.notes)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    formal.change('validFrom', initialValues.validFrom)
    formal.change('validTill', initialValues.validTill)
    formal.change('count', initialValues.count)
    formal.change('plateNumbers', initialValues.plateNumbers)
    formal.change('clients', initialValues.clients)
    formal.change('notes', initialValues.notes)
    formal.clearErrors()
    setLinked(false)
  }

  const handleValidFromChange = (value: string) => {
    formal.change('validFrom', value)

    if (linked) {
      formal.change('validTill', deriveValidTillField(value, data.durationType, data.durationLength))
    }
  }

  const handleToggleLinked = () => {
    if (!linked) {
      formal.change('validTill', deriveValidTillField(formal.values.validFrom, data.durationType, data.durationLength))
    }

    setLinked(!linked)
  }

  const initialValues = {
    validFrom: '',
    validTill: '',
    count: 1,
    plateNumbers: '',
    clients: '',
    notes: '',
  }

  const schema = yup.object().shape({
    validFrom: yupDateField({ required: updateValidPeriod }),
    validTill: yupDateField(),
    count: yup.number().required().integer().min(0).max(1000),
    plateNumbers: yup.string().matches(/^[0-9a-zA-Z, \n]*$/),
    clients: yup.string().trim().matches(/^[0-9a-zA-Z., ]*$/),
    notes: yup.string().trim().max(200),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.updateSubscription({
        subscription_id: data.id,
        ...(updateValidPeriod && {
          from: formatDateFieldToISO(values.validFrom),
          till: values.validTill ? formatDateFieldToISO(values.validTill) : null,
        }),
        ...(updateCount && {
          count: values.count,
        }),
        plate_numbers: values.plateNumbers ? values.plateNumbers.split(/[,\n]/).map(plateNumber => plateNumber.replace(/\s/g, '').toUpperCase()).filter((plateNumber, index, self) => plateNumber && self.indexOf(plateNumber) === index) : [],
        ...(updateClients && {
          clients: values.clients ? values.clients.split(/[,]/).map(client => client.trim()).filter((client, index, self) => client && self.indexOf(client) === index) : [],
        }),
        notes: values.notes,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()

          setLoadList()

          showSuccess(t('messages.subscriptionUpdated'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            case 6:
              message = t('alerts.maxSubscriptionsReached')
              break
            case 16:
              message = t('alerts.subscriptionFromDateAfterFirstSessionDay', { date: formatToDateField(error.extra.firstSessionDay) })
              break
            case 9:
              message = t('alerts.maxSubscriptionVehiclesReached')
              break
            case 10:
              message = t('alerts.subscriptionVehicleOverlap', { plates: error.extra.plates.join(', ') })
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotUpdateSubscription')} ${message}`)
        })
    },
  })

  const validTill = linked && deriveValidTill(formal.values.validFrom, data.durationType, data.durationLength)
  const endTime = validTill ? format(validTill, 'dd.MM.yyyy HH:mm') : ''

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose} TransitionProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('editSubscription')}</DraggableDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {updateValidPeriod && <>
              <DateField
                GridProps={linkable ? { xs: 12, sm: true } : { xs: 6 }}
                label={t('startDate')}
                required
                autoFocus
                {...(linked && data.durationType === DurationType.monthsAbsolute && {
                  format: '01.MM.yyyy',
                  views: ['year', 'month'],
                  openTo: 'month',
                })}
                {...(!linked && formal.values.validTill && { maxDate: parseDateField(formal.values.validTill) })}
                {...formal.getFieldProps('validFrom')}
                onChange={e => handleValidFromChange(e.target.value)}
              />
              {linkable && (
                <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton
                    size="small"
                    color={linked ? 'primary' : 'default'}
                    onClick={handleToggleLinked}
                    disabled={formal.isSubmitting}
                  >
                    {linked ? <IconLink /> : <IconLinkOff />}
                  </IconButton>
                </Grid>
              )}
              {linked ? (
                <Grid item xs>
                  <EndTimeField
                    label={t('endTime')}
                    value={endTime}
                    fullWidth
                    variant="outlined"
                    disabled={true}
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
              ) : (
                <DateField
                  GridProps={linkable ? { xs: true } : { xs: 6 }}
                  label={t('endDate')}
                  {...(formal.values.validFrom && { minDate: parseDateField(formal.values.validFrom) })}
                  clearable
                  {...formal.getFieldProps('validTill')}
                />
              )}
            </>}
            {updateCount && (
              <TextField
                GridProps={{ xs: 12 }}
                type="number"
                label={t('quantity')}
                required
                inputProps={{
                  min: 0,
                  max: 1000,
                }}
                {...formal.getFieldProps('count')}
              />
            )}
            <TextField
              GridProps={{ xs: 12 }}
              label={t('plateNumbers')}
              multiline
              autoComplete="off"
              helperText={`${t('example')}: AB1234, CD4567`}
              inputProps={{
                style: {
                  fontFamily: '"Roboto Mono", monospace',
                  lineHeight: '21px',
                  textTransform: 'uppercase',
                  marginTop: -1,
                  marginBottom: -1,
                  maxHeight: 63,
                  overflow: 'auto',
                },
              }}
              {...(!updateValidPeriod && { autoFocus: true })}
              {...formal.getFieldProps('plateNumbers')}
            />
            {updateClients && <TextField
              GridProps={{ xs: 12 }}
              label={t('client')}
              helperText={t('clientPhoneNumberOrUsernameHelperText')}
              {...formal.getFieldProps('clients')}
            />}
            <TextField
              GridProps={{ xs: 12 }}
              label={t('notes')}
              inputProps={{ maxLength: 200 }}
              {...formal.getFieldProps('notes')}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formal.isSubmitting}
          >
            {t('edit')}
          </Button>
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default EditSubscriptionDialog
